import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Drawer, Form, Input, InputNumber, Select, Spin } from "antd";
import PropTypes from "prop-types";

import { Modals, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

import UploadImage from "components/UploadImage";

const Component = ({ data = {}, onUpdate, options: { images = [] } }) => {
	const initialState = {};
	const [state, setState] = useState(initialState);
	const { loading, submitting, visible } = state;
	const { id, fullName } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleCompare = (rule, value) => {
		return !value || form.getFieldValue("password") === value
			? Promise.resolve()
			: Promise.reject(Resources.comparePass);
	};

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values.avatarId = values.avatar?.id;

				delete values.avatar;

				const action =
					hash === `#${Modals.CREATE}`
						? API.user.create(values)
						: API.user.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, submitting: false }));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		form.setFieldValue("deviceCountLimitation", 1);
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				form.setFieldValue("password", "12345678");
				form.setFieldValue("passwordConfirmation", "12345678");

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({ ...prevState, visible: true }));

					form.setFieldsValue(data);
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	return (
		<Drawer
			className="sp-form-drawer"
			placement={process.env.REACT_APP_IS_RTL ? "left" : "right"}
			footer={
				<>
					<Button
						type="primary"
						onClick={handleSubmit}
						loading={submitting}
					>
						{Resources.confirm}
					</Button>
					<Button
						onClick={() => navigate(-1)}
						disabled={submitting}
					>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => navigate(-1)}
			title={
				hash === `#${Modals.CREATE}`
					? `${Resources.add} ${Resources.user}`
					: `${Resources.edit} ${Resources.user} "${fullName || "-"}"`
			}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form
				layout="vertical"
				form={form}
				autoComplete="off"
			>
				{loading ? (
					<Spin className="sp-center-spin" />
				) : (
					<>
						<Form.Item
							name="firstName"
							label={Resources.name}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="lastName"
							label={Resources.lastName}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
						<Form.Item
							name="phone"
							label={Resources.mobile}
							rules={Rules.required}
						>
							<Input
								type="tel"
								autoComplete="off"
							/>
						</Form.Item>
						<Form.Item
							name="avatar"
							label={Resources.image}
						>
							{/* <UploadImage section={Types.upload.user} /> */}
							<Select
								options={images}
								optionFilterProp="label"
								showSearch
							/>
						</Form.Item>
						<hr
							className="sp-divider"
							style={{ marginBottom: 16 }}
						/>
						{hash === `#${Modals.CREATE}` ? (
							<>
								<Form.Item
									name="password"
									label={Resources.password}
									rules={Rules.required.concat(Rules.password)}
								>
									<Input.Password
										type="password"
										autoComplete="off"
									/>
								</Form.Item>
								<Form.Item
									name="passwordConfirmation"
									label={`${Resources.repeat} ${Resources.password}`}
									rules={Rules.required.concat([{ validator: handleCompare }])}
								>
									<Input.Password
										type="password"
										autoComplete="off"
									/>
								</Form.Item>
							</>
						) : (
							<>
								<Form.Item
									name="password"
									label={`${Resources.password} ${Resources.new}`}
									rules={Rules.password}
								>
									<Input.Password type="password" />
								</Form.Item>
								<Form.Item
									name="passwordConfirmation"
									label={`${Resources.repeat} ${Resources.password} ${Resources.new}`}
									rules={[{ validator: handleCompare }]}
								>
									<Input.Password type="password" />
								</Form.Item>
							</>
						)}
					</>
				)}
				<Form.Item
					name="deviceCountLimitation"
					label={`${Resources.count} ${Resources.device} ${Resources.user}`}
					rules={Rules.required}
				>
					<InputNumber />
				</Form.Item>
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
};

export default Component;
